import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from 'react';
import { Button, Checkbox, Form, Input, Upload, App, Flex, Tooltip, } from 'antd';
import { UploadOutlined, InfoCircleOutlined } from '@ant-design/icons';
import FormModal from 'components/FormModal';
import { txt } from 'libs/i18n';
import fileUploadStyles from 'components/SingleFileUpload/style.module.less';
import { useSession } from 'components/Authenticated';
import clsx from 'clsx';
import { useDashboard } from 'screens/DashboardScreen/dashboard-context';
import { sendBugReport } from 'api/user-api';
import { fromByteArray } from 'base64-js';
import { detectBrowser, resolveOperatingSystem } from 'libs/browser';
import { getCurrentUiLocale } from 'libs/locales/ui';
import FileItem from './FileItem';
import styles from './style.module.less';
const { TextArea } = Input;
const { Dragger } = Upload;
const MAX_FILES_NUMBER = 5;
const BugReportModal = ({ open, onCancel }) => {
    const [form] = Form.useForm();
    const [fileList, setFileList] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { session } = useSession();
    const { message } = App.useApp();
    const { projectId, url } = useDashboard();
    const projectIdByUser = Form.useWatch('projectId', form);
    const hasProjectId = projectIdByUser !== undefined && projectIdByUser !== '';
    const createAttachments = async () => {
        const attachments = await Promise.all(fileList.map(async (file) => {
            var _a, _b;
            const arrayBuffer = await ((_a = file.originFileObj) === null || _a === void 0 ? void 0 : _a.arrayBuffer());
            if (arrayBuffer === undefined) {
                throw new Error(`Failed to read file: ${file.name}`);
            }
            const base64 = fromByteArray(new Uint8Array(arrayBuffer));
            return {
                fileName: file.name,
                contentType: (_b = file.type) !== null && _b !== void 0 ? _b : 'unknown',
                contentBase64: base64,
            };
        }));
        return attachments;
    };
    const handleCancel = () => {
        form.resetFields();
        setFileList([]);
        onCancel === null || onCancel === void 0 ? void 0 : onCancel();
    };
    const handleSubmit = async (values) => {
        var _a, _b;
        setIsSubmitting(true);
        try {
            const { description, shareWithSupport } = values;
            const browser = detectBrowser();
            const currentUiLocale = getCurrentUiLocale();
            const operatingSystem = await resolveOperatingSystem();
            const bugReport = {
                description,
                projectId: Number(values.projectId),
                shareWithSupport,
                section: url !== null && url !== void 0 ? url : 'Unknown',
                attachments: await createAttachments(),
                operatingSystem,
                browser: `${(_a = browser === null || browser === void 0 ? void 0 : browser.name) !== null && _a !== void 0 ? _a : 'unknown'} ${(_b = browser === null || browser === void 0 ? void 0 : browser.version) !== null && _b !== void 0 ? _b : ''}`,
                applicationVersion: VERSION,
                localization: currentUiLocale.code,
            };
            const result = await sendBugReport(session.connection, bugReport);
            if (result.isFail()) {
                if (result.err() === 'validation-failed') {
                    void message.error(txt('validationFailed'));
                }
                else if (result.err() === 'rate-limit-exceeded') {
                    void message.error(txt('rateLimitExceeded'));
                }
                else {
                    void message.error(txt('unexpectedError'));
                }
            }
            else {
                void message.success(txt('bugReportSent'));
            }
            handleCancel();
        }
        finally {
            setIsSubmitting(false);
        }
    };
    const handleChange = (info) => {
        setFileList(info.fileList);
        void form.validateFields(['screenshots']);
    };
    const validateScreenshots = () => {
        if (fileList.length === 0) {
            return Promise.resolve();
        }
        if (fileList.length > MAX_FILES_NUMBER) {
            return Promise.reject(new Error(txt('maxFilesExceeded')));
        }
        const invalidType = fileList.find((file) => {
            const originFile = file.originFileObj;
            return originFile !== undefined && !(originFile.type === 'image/png' || originFile.type === 'image/jpeg');
        });
        if (invalidType !== undefined) {
            return Promise.reject(new Error(txt('invalidFileType')));
        }
        const tooLarge = fileList.find((file) => {
            const originFile = file.originFileObj;
            return originFile !== undefined && (originFile.size / 1024 / 1024) >= 10;
        });
        if (tooLarge !== undefined) {
            return Promise.reject(new Error(txt('fileSizeTooLarge')));
        }
        return Promise.resolve();
    };
    const isUploadLimited = fileList.length >= MAX_FILES_NUMBER;
    return (_jsx(FormModal, { className: styles.bugReportModal, title: txt('reportBug'), open: open, onCancel: handleCancel, footer: [
            _jsx(Button, { onClick: handleCancel, children: txt('cancel') }, "cancel"),
            _jsx(Button, { type: "primary", onClick: () => form.submit(), loading: isSubmitting, children: txt('submitReport') }, "submit"),
        ], children: _jsxs(Form, { form: form, layout: "vertical", onFinish: handleSubmit, initialValues: {
                description: '',
                shareWithSupport: true,
                projectId: projectId !== undefined ? Number(projectId) : undefined,
            }, children: [_jsx(Form.Item, { name: "description", label: (_jsxs(Flex, { vertical: true, children: [_jsx("span", { children: txt('whatHappened') }), _jsx("span", { className: styles.extra, children: txt('beAsSpecificAsPossible') })] })), rules: [{ required: true, message: txt('pleaseDescribeIssue') }], children: _jsx(TextArea, { placeholder: txt('bugDescriptionPlaceholder'), showCount: true, maxLength: 5000, autoSize: { minRows: 6, maxRows: 10 }, autoFocus: true }) }), _jsx(Form.Item, { name: "projectId", label: (_jsxs(_Fragment, { children: [txt('projectID'), _jsx(Tooltip, { title: txt('whereProjectID'), children: _jsx(InfoCircleOutlined, { className: styles.infoCircle }) })] })), style: { maxWidth: '200px' }, validateTrigger: ['onChange', 'onBlur'], 
                    // NOTE This is necessary because of Firefox which allows to enter text to InputNumber
                    rules: [
                        {
                            validator: (_, value) => {
                                if (value === undefined || value === null || value === '') {
                                    return Promise.resolve();
                                }
                                if (!/^\d+$/.test(String(value))) {
                                    return Promise.reject(new Error(txt('pleaseEnterValidNumber')));
                                }
                                return Promise.resolve();
                            },
                        },
                    ], children: _jsx(Input, {}) }), _jsx(Form.Item, { name: "screenshots", label: txt('screenshotsOptional'), rules: [{
                            validator: validateScreenshots,
                        }], children: _jsxs("div", { className: styles.uploadSection, children: [_jsxs(Dragger, { disabled: isUploadLimited, className: clsx(fileUploadStyles.fileDragger, {
                                    [styles.draggerDisabled]: isUploadLimited,
                                }), showUploadList: false, onChange: handleChange, beforeUpload: () => false, accept: ".png,.jpg,.jpeg", multiple: true, fileList: fileList, children: [_jsx(UploadOutlined, { className: fileUploadStyles.fileDraggerIcon }), _jsx("p", { children: txt('dragAndDropScreenshots') }), _jsxs("p", { className: fileUploadStyles.fileDraggerHint, children: [`${fileList.length}/5 ${txt('files')}`, ' • ', txt('pngJpgUpTo10MB')] })] }), fileList.length > 0 && (_jsx("div", { className: styles.fileList, children: fileList.map((file) => (_jsx(FileItem, { file: file, onRemove: (fileToRemove) => {
                                        setFileList(fileList.filter((f) => f.uid !== fileToRemove.uid));
                                        void form.validateFields(['screenshots']);
                                    } }, file.uid))) }))] }) }), hasProjectId && (_jsx(Form.Item, { name: "shareWithSupport", valuePropName: "checked", children: _jsx(Checkbox, { children: txt('shareProjectWithCustomerSupport') }) }))] }) }));
};
export default BugReportModal;
